import { type ReactNode } from 'react'
import { CheckCircle, XCircle, AlertTriangle, Info } from 'lucide-react'

/* ==========================================================================
   Alert Component
   Based on design.md specifications
   ========================================================================== */

export type AlertVariant = 'success' | 'error' | 'warning' | 'info'

export interface AlertProps {
  /** Alert variant determines colors and icon */
  variant: AlertVariant
  /** Alert message content */
  children: ReactNode
  /** Optional title displayed above message */
  title?: string
  /** Additional CSS classes */
  className?: string
}

const variantStyles: Record<
  AlertVariant,
  { bg: string; border: string; icon: string }
> = {
  success: {
    bg: 'bg-[var(--color-success-light)]',
    border: 'border-l-[var(--color-success)]',
    icon: 'text-[var(--color-success)]',
  },
  error: {
    bg: 'bg-[var(--color-error-light)]',
    border: 'border-l-[var(--color-error)]',
    icon: 'text-[var(--color-error)]',
  },
  warning: {
    bg: 'bg-[var(--color-warning-light)]',
    border: 'border-l-[var(--color-warning)]',
    icon: 'text-[var(--color-warning)]',
  },
  info: {
    bg: 'bg-[var(--color-info-light)]',
    border: 'border-l-[var(--color-info)]',
    icon: 'text-[var(--color-info)]',
  },
}

const variantIcons: Record<AlertVariant, typeof CheckCircle> = {
  success: CheckCircle,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
}

/**
 * Inline alert component with left border accent and icon.
 *
 * @example
 * ```tsx
 * <Alert variant="success">Operation completed successfully.</Alert>
 *
 * <Alert variant="error" title="Error">
 *   Something went wrong. Please try again.
 * </Alert>
 *
 * <Alert variant="warning">
 *   Your session will expire in 5 minutes.
 * </Alert>
 *
 * <Alert variant="info" title="Note">
 *   This feature is in beta.
 * </Alert>
 * ```
 */
export function Alert({
  variant,
  children,
  title,
  className = '',
}: AlertProps) {
  const styles = variantStyles[variant]
  const Icon = variantIcons[variant]

  return (
    <div
      className={`flex gap-3 p-4 rounded-md border-l-4 ${styles.bg} ${styles.border} ${className}`}
      role="alert"
    >
      <Icon
        className={`h-5 w-5 flex-shrink-0 mt-0.5 ${styles.icon}`}
        aria-hidden="true"
      />
      <div className="flex-1 min-w-0">
        {title && <p className="font-medium text-charcoal mb-1">{title}</p>}
        <div className="text-sm text-charcoal">{children}</div>
      </div>
    </div>
  )
}

export default Alert
